/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import dayjs from "dayjs";

import { When } from "react-if";
import {
  Box,
  FlexContainer,
  MultipleSelect,
  Select,
  SelectButton,
  Text,
  theme,
} from "@nordcloud/gnui";
import { DateRange, DateRangeSelector } from "~/components";
import { EstatePeriod, dateFormat } from "~/constants";
import { isNonEmpty } from "~/tools";
import { defaultRangeLabel, defaultSelectButtonWidth } from "./constants";
import { ReservationType } from "./types";

type Props = {
  period: EstatePeriod;
  range?: DateRange;
  reservationIds?: ReservationType[];
  selectedReservationId?: ReservationType | null;
  onChangeRange: (range?: DateRange) => void;
  onChangePeriod: (periodName: EstatePeriod) => void;
  onChangeReservationId: (newValue: ReservationType | null) => void;
};

export function UtilizationHeader({
  period,
  range,
  reservationIds,
  onChangeRange,
  onChangePeriod,
  onChangeReservationId,
  selectedReservationId,
}: Props) {
  const [rangeLabel, setRangeLabel] = useState(defaultRangeLabel);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const hasReservationIds = isNonEmpty(reservationIds);
  const shouldPreselect = hasReservationIds && reservationIds.length === 1;
  const reservationIdValue = shouldPreselect
    ? reservationIds[0]
    : selectedReservationId;
  const setRange = (selectedRange: DateRange | undefined) => {
    if (period === EstatePeriod.DEFAULT_MONTH && selectedRange) {
      selectedRange.to = dayjs().format(dateFormat.shortDate).toString();
    }

    onChangeRange(selectedRange);
    onChangePeriod(EstatePeriod.RANGE);
  };

  const handleClear = () => {
    setRangeLabel(defaultRangeLabel);
  };

  const onChangeCurrentMonth = () => {
    const newRange = {
      from: dayjs().startOf("month").format(dateFormat.shortDate).toString(),
      to: dayjs().endOf("month").format(dateFormat.shortDate).toString(),
    };

    onChangePeriod(EstatePeriod.DEFAULT_MONTH);
    onChangeRange(newRange);
    handleClear();
  };

  const onChangePreviousMonth = () => {
    const previousMonth = dayjs().subtract(1, "month");
    const newRange = {
      from: previousMonth
        .startOf("month")
        .format(dateFormat.shortDate)
        .toString(),
      to: previousMonth.endOf("month").format(dateFormat.shortDate).toString(),
    };

    onChangeRange(newRange);
    onChangePeriod(EstatePeriod.PREVIOUS_MONTH);
    handleClear();
  };

  useEffect(() => {
    if (isInitialLoad) {
      const startOfMonth = dayjs()
        .startOf("month")
        .format(dateFormat.shortDate)
        .toString();
      const endOfMonth = dayjs()
        .endOf("month")
        .format(dateFormat.shortDate)
        .toString();

      const newRange = {
        from: startOfMonth,
        to: endOfMonth,
      };

      onChangeRange(newRange);
      setIsInitialLoad(false);
    }
    if (period === EstatePeriod.RANGE) {
      const label = `${range?.from}-${range?.to}`;
      setRangeLabel(label);
    }
  }, [isInitialLoad, onChangeRange, period, range?.from, range?.to]);

  return (
    <>
      <Box boxStyle="lightGrey">
        <FlexContainer alignItems="center" wrap="wrap">
          <FlexContainer
            css={{
              paddingBottom: theme.spacing.spacing01,
              marginRight: theme.spacing.spacing07,
            }}
          >
            <Text size="sm" mb="0" mr={theme.spacing.spacing04} weight="medium">
              Term:
            </Text>
            <MultipleSelect size="small">
              <SelectButton
                isActive={period === EstatePeriod.PREVIOUS_MONTH}
                name="previousMonth"
                value={dayjs().subtract(1, "month").month().toString()}
                labelText={dayjs().subtract(1, "month").format("MMM")}
                style={{
                  minWidth: defaultSelectButtonWidth,
                }}
                onClick={onChangePreviousMonth}
              />
              <SelectButton
                name="currentMonth"
                value={dayjs().month().toString()}
                labelText={dayjs().format("MMM")}
                isActive={period === EstatePeriod.DEFAULT_MONTH}
                style={{
                  minWidth: defaultSelectButtonWidth,
                }}
                onClick={onChangeCurrentMonth}
              />

              <DateRangeSelector
                showPreselects
                isActive={period === EstatePeriod.RANGE}
                label={rangeLabel}
                period={period}
                range={range}
                onApply={setRange}
                onClear={handleClear}
              />
            </MultipleSelect>
          </FlexContainer>
          <When condition={hasReservationIds}>
            <FlexContainer
              paddingBottom={theme.spacing.spacing01}
              marginRight={theme.spacing.spacing04}
            >
              <Text
                size="sm"
                mb="0"
                mr={theme.spacing.spacing04}
                weight="medium"
              >
                Reservations:
              </Text>

              <Select
                name="Reservations List"
                options={reservationIds}
                value={reservationIdValue}
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: "16rem",
                  }),
                }}
                onChange={onChangeReservationId}
              />
            </FlexContainer>
          </When>
        </FlexContainer>
      </Box>
    </>
  );
}
